import type { Registry } from "@/api.js"
import { type EditorRule, laneOf } from "./model.js"

/**
 * A run of consecutive rules reading the same sources, in execution order.
 *
 * **The list is grouped by source because that is how a person thinks about
 * the rules, and the order the editor holds keeps the sources together** —
 * `fromDocument` sorts a lane by `sourceRank` before the stored priority, so a
 * run here is the whole of a source's rules in that lane and a run of one is a
 * source with one rule. Priority still encodes the cost of a rule's source and
 * nothing else; the order between two sources in the same lane decides only
 * which finding is written first, because every source in the lane is already
 * in flight before the first rule runs. The walk is still over the flat list
 * as it stands, so an order that somehow interleaves is shown as it is, with
 * a heading repeated, rather than quietly resorted.
 *
 * A rule reading two sources is its own group, titled with both; a rule reading
 * none is grouped as such. Neither exists in this build, and neither would be
 * hidden if it did.
 */
export interface RuleGroup {
  /** The sources, as one key: what decides whether two neighbours share a group. */
  readonly key: string
  readonly providerIds: readonly string[]
  /** The source's own title, from the registry — what the heading says. */
  readonly title: string
  /** The cost lane every rule in the group runs in. */
  readonly lane: number
  readonly rules: readonly EditorRule[]
}

export function groupBySource(rules: readonly EditorRule[], registry: Registry): RuleGroup[] {
  const groups: (Omit<RuleGroup, "rules"> & { rules: EditorRule[] })[] = []

  for (const rule of rules) {
    const key = sourceKey(rule)
    const last = groups.at(-1)
    if (last !== undefined && last.key === key) {
      last.rules.push(rule)
      continue
    }
    groups.push({
      key,
      providerIds: rule.dependsOn,
      title: titleOf(rule.dependsOn, registry),
      lane: laneOf(rule, registry),
      rules: [rule],
    })
  }

  return groups
}

export function sourceKey(rule: EditorRule): string {
  return rule.dependsOn.length === 0 ? "" : [...rule.dependsOn].sort().join("+")
}

function titleOf(providerIds: readonly string[], registry: Registry): string {
  if (providerIds.length === 0) return "Bez źródła"
  return providerIds
    .map(
      (providerId) =>
        registry.providers.find((provider) => provider.providerId === providerId)?.title ??
        providerId,
    )
    .join(" + ")
}
